import type { ColumnDef } from "@tanstack/react-table";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Copy, MoreHorizontal, Power, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTableColumnHeader } from "@/components/data-table";
import { cn } from "@/lib/utils";
import type { User } from "@/types/user";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserColumnsOptions {
  canImpersonateTarget: (target: User) => boolean;
  onLoginAs: (target: User) => void;
  canManageTarget: (target: User) => boolean;
  onToggleActive: (target: User) => void;
  onDelete: (target: User) => void;
}

export function getUserColumns(
  i18n: I18n,
  options: UserColumnsOptions,
): ColumnDef<User, unknown>[] {
  return [
    {
      id: "name",
      accessorFn: (row) =>
        `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim() || "—",
      meta: { label: i18n._(msg`Name`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Name`)} />
      ),
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: "email",
      meta: { label: i18n._(msg`Email`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Email`)} />
      ),
      cell: ({ getValue }) => {
        const email = getValue() as string;

        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
              aria-label={i18n._(msg`Copy email`)}
              onClick={(event) => {
                event.stopPropagation();
                const promise = navigator.clipboard?.writeText(email);
                if (promise) void promise.catch(() => {});
              }}
            >
              <Copy className="size-4" />
            </button>
            <span className="text-sm text-(--color-text-secondary)">{email}</span>
          </div>
        );
      },
    },
    {
      id: "organization",
      accessorFn: (row) => row.organization?.name ?? "",
      meta: { label: i18n._(msg`Organization`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Organization`)} />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {row.original.organization?.name ?? "—"}
        </span>
      ),
    },
    {
      id: "company",
      accessorFn: (row) => row.company?.name ?? "",
      meta: { label: i18n._(msg`Concept`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Concept`)} />
      ),
      cell: ({ row }) => (
        <span className="text-sm text-(--color-text-secondary)">
          {row.original.company?.name ?? "—"}
        </span>
      ),
    },
    {
      id: "role",
      accessorFn: (row) => {
        const primary =
          row.userRoles?.find((ur) => ur.role.code !== "SUPER_ADMIN") ??
          row.userRoles?.[0];
        return primary?.role.name ?? "";
      },
      meta: { label: i18n._(msg`Role`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Role`)} />
      ),
      cell: ({ row }) => {
        const primary =
          row.original.userRoles?.find((ur) => ur.role.code !== "SUPER_ADMIN") ??
          row.original.userRoles?.[0];
        return (
          <span className="text-sm text-(--color-text-secondary)">
            {primary?.role.name ?? "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "isActive",
      meta: { label: i18n._(msg`Status`) },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={i18n._(msg`Status`)} />
      ),
      cell: ({ getValue }) => {
        const active = getValue() as boolean;
        return (
          <span
            className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium",
              active ? "bg-(--color-status-active) text-(--brand-white)" : "bg-(--color-surface-accent) text-(--color-text-secondary)",
            )}
          >
            {active ? i18n._(msg`Active`) : i18n._(msg`Inactive`)}
          </span>
        );
      },
    },
    {
      id: "impersonation",
      meta: { label: i18n._(msg`Login as`) },
      header: i18n._(msg`Login as`),
      cell: ({ row }) => {
        const target = row.original;
        if (!options.canImpersonateTarget(target)) {
          return <span className="text-sm text-(--color-text-secondary)">-</span>;
        }

        return (
          <Button
            size="sm"
            variant="outline"
            onClick={() => options.onLoginAs(target)}
          >
            {i18n._(msg`Login as`)}
          </Button>
        );
      },
      enableSorting: false,
    },
    {
      id: "actions",
      meta: { label: i18n._(msg`Actions`) },
      header: i18n._(msg`Actions`),
      cell: ({ row }) => {
        const target = row.original;
        if (!options.canManageTarget(target)) {
          return <span className="text-sm text-(--color-text-secondary)">-</span>;
        }

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{i18n._(msg`Actions`)}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault();
                  options.onToggleActive(target);
                }}
              >
                <Power className="mr-2 h-4 w-4" />
                {target.isActive ? i18n._(msg`Set as inactive`) : i18n._(msg`Set as active`)}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-(--color-danger-text) focus:text-(--color-danger-text)"
                onSelect={(event) => {
                  event.preventDefault();
                  options.onDelete(target);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {i18n._(msg`Delete`)}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
    },
  ];
}
